import type { CircuitRowResponse, EarthFaultLoopResponse } from "../services/earthFaultLoop";
import type { ExternalImpedanceOrigin, LimitBasis } from "../services/earthFaultLoopContract";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import { STATUS_LABELS as UPS_STATUS_LABELS, STATUS_TONE as UPS_STATUS_TONE } from "./UPSResultSummary";
import { StatusIcon } from "./StatusIcon";
import { bannerTone } from "./statusTone";

// The same words and the same colour as the other study summaries for a calculated, a
// warned and a review-required study; the NO_SOLUTION words are this module's own: a circuit
// whose disconnection is not assured is a failed check (A33, section 8).
const STATUS_LABELS: Record<EarthFaultLoopResponse["status"], string> = {
  ...UPS_STATUS_LABELS,
  NO_SOLUTION: "Disconnection not assured",
};

const STATUS_TONE: Record<EarthFaultLoopResponse["status"], "pass" | "fail" | "warning"> =
  UPS_STATUS_TONE;

export const ZE_ORIGIN_LABELS: Record<ExternalImpedanceOrigin, string> = {
  LINKED_FAULT_RUN: "Linked earth-fault run",
  DECLARED: "Declared value",
  NOT_ESTABLISHED: "Not established",
};

export const LIMIT_BASIS_LABELS: Record<LimitBasis, string> = {
  REGISTER_VERIFIED: "Register-verified table (CPWD App F)",
  DECLARED: "Declared device data (not verified)",
  NOT_ESTABLISHED: "Not established",
};

export type RowKind = "met" | "not-met" | "not-compared" | "review";

/**
 * How one circuit fared: not met is a failed condition; a row whose loop or device figure
 * is missing was not compared; a met row still under review (a declared limit, a missing
 * source) is a review; a met row with nothing owed is met.
 */
export function rowKind(row: CircuitRowResponse): RowKind {
  if (row.disconnection_assured === false) {
    return "not-met";
  }
  if (row.disconnection_assured === null) {
    return "not-compared";
  }
  return row.status === "VALID" || row.status === "WARNING" ? "met" : "review";
}

/** "Disconnection not assured for 1 circuit" / "... for 3 circuits"; the other statuses' words. */
export function statusHeading(result: EarthFaultLoopResponse): string {
  if (result.status !== "NO_SOLUTION") {
    return STATUS_LABELS[result.status];
  }
  const notMet = result.rows.filter((row) => row.disconnection_assured === false).length;
  return `${STATUS_LABELS.NO_SOLUTION} for ${notMet} ${notMet === 1 ? "circuit" : "circuits"}`;
}

/**
 * The compared row with the smallest margin Zs_max - Zs, or null when no row was compared.
 * The margins are the engine's exact strings; they are only compared here, never recomputed.
 */
export function tightestMargin(rows: readonly CircuitRowResponse[]): CircuitRowResponse | null {
  let tightest: CircuitRowResponse | null = null;
  for (const row of rows) {
    if (row.loop_impedance_margin_ohm === null) {
      continue;
    }
    if (
      tightest === null ||
      Number(row.loop_impedance_margin_ohm) < Number(tightest.loop_impedance_margin_ohm)
    ) {
      tightest = row;
    }
  }
  return tightest;
}

type EarthFaultLoopResultSummaryProps = {
  result: EarthFaultLoopResponse;
};

// The decisive values first (Master Prompt v2.1 section 19): how the circuits fared, Ze and
// where it came from, U0 and the comparison voltage, the tightest margin and the limit basis.
// No figure is calculated here. Display rule A12: four significant figures on screen, the
// exact engine decimal in the tooltip (title attribute).
export function EarthFaultLoopResultSummary({ result }: EarthFaultLoopResultSummaryProps) {
  const tone = STATUS_TONE[result.status];
  const rows = result.rows;
  const kinds = rows.map(rowKind);
  const met = kinds.filter((kind) => kind === "met").length;
  const notMet = kinds.filter((kind) => kind === "not-met").length;
  const review = kinds.filter((kind) => kind === "review").length;
  const notCompared = kinds.filter((kind) => kind === "not-compared").length;
  const warningCount =
    result.warnings.length + rows.reduce((count, row) => count + row.warnings.length, 0);

  const ze = formatQuantity(result.external_loop_impedance_ohm, "Ω");
  const u0 = formatQuantity(result.line_to_earth_voltage_v, "V");
  const comparison = formatQuantity(result.comparison_voltage_v, "V");
  const tightest = tightestMargin(rows);
  const margin = formatQuantity(tightest?.loop_impedance_margin_ohm ?? null, "Ω");

  return (
    <section aria-label="Result summary" data-summary-tone={tone}>
      <header>
        <p data-summary-status={result.status}>
          <StatusIcon tone={bannerTone(result.status, STATUS_TONE)} size={20} />
          {statusHeading(result)}
        </p>
        <p>
          Study <strong>{result.code}</strong> · {result.name} · {result.jurisdiction_profile}{" "}
          profile
        </p>
      </header>
      <dl data-summary-strip>
        <div>
          <dt>Circuits</dt>
          {/* Counts of row outcomes, not engineering quantities. */}
          <dd data-summary-field="circuits" data-summary-value="text">
            {rows.length} checked · {met} met · {notMet} not met · {review} review ·{" "}
            {notCompared} not compared
          </dd>
        </div>
        <div>
          <dt>External impedance Ze</dt>
          <dd data-summary-field="external-impedance" title={ze.exact ?? undefined}>
            {result.external_loop_impedance_ohm === null ? EMPTY_QUANTITY : ze.display} at{" "}
            {result.bus_code}
          </dd>
        </div>
        <div>
          <dt>Ze source</dt>
          <dd
            data-summary-field="external-impedance-origin"
            data-summary-value="text"
            data-external-impedance-origin={result.external_impedance_origin}
          >
            {ZE_ORIGIN_LABELS[result.external_impedance_origin]}
          </dd>
        </div>
        <div>
          <dt>U0 · comparison voltage</dt>
          <dd data-summary-field="voltage" title={`${result.line_to_earth_voltage_v} V · ${result.comparison_voltage_v} V`}>
            {u0.display} · {comparison.display}
          </dd>
        </div>
        <div>
          <dt>Tightest margin Zs_max − Zs</dt>
          {/* The engine's margin of the tightest compared circuit; negative when not met. */}
          <dd data-summary-field="tightest-margin" title={margin.exact ?? undefined}>
            {tightest === null ? EMPTY_QUANTITY : `${margin.display} (${tightest.code})`}
          </dd>
        </div>
        <div>
          <dt>Limit basis</dt>
          <dd data-summary-field="limit-basis" data-summary-value="text" data-limit-basis={result.limit_basis}>
            {LIMIT_BASIS_LABELS[result.limit_basis]}
          </dd>
        </div>
        <div>
          <dt>Warnings</dt>
          <dd data-summary-warnings={warningCount}>{warningCount}</dd>
        </div>
      </dl>
    </section>
  );
}

export { STATUS_LABELS, STATUS_TONE };
export default EarthFaultLoopResultSummary;
